import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "421",
  "slug": "what-are-the-tropics-of-cancer-and-capricorn",
  "title": "What Are the Tropic of Cancer and Tropic of Capricorn?",
  "excerpt": "These two famous lines mark the farthest the Sun ever climbs overhead. Here is what they are, why they sit at about 23.5 degrees, and why they have such odd names.",
  "date": "2026-06-12",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1555979864-7a8f9b4fddf8?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMGNvYXN0bGluZXxlbnwxfDB8fHwxNzgxMjUyMzA3fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "The Tropic of Cancer (about 23.5 degrees north) and the Tropic of Capricorn (about 23.5 degrees south) mark the farthest north and south the Sun ever appears directly overhead. The band between them, the Tropics, is the only part of Earth that ever gets a truly vertical noon sun.",
  "content": [
    {
      "type": "paragraph",
      "text": "On most world maps you will find two horizontal lines drawn at equal distances above and below the equator, labelled the Tropic of Cancer and the Tropic of Capricorn. They are not borders or coastlines. They are astronomical lines, and they mark something specific and surprisingly important: the limit of how far the Sun ever travels overhead."
    },
    {
      "type": "heading",
      "text": "What the lines actually mark"
    },
    {
      "type": "paragraph",
      "text": "The Tropic of Cancer, at roughly 23.5 degrees north, is the most northerly latitude where the Sun can ever appear directly overhead, at the zenith, at noon. That happens just once a year, at the June solstice. The Tropic of Capricorn, at roughly 23.5 degrees south, is the mirror image: the most southerly latitude where the Sun stands directly overhead, which happens at the December solstice. Anywhere outside these two lines, the Sun is always at least slightly to the south or north at midday and never truly overhead."
    },
    {
      "type": "heading",
      "text": "Why 23.5 degrees?"
    },
    {
      "type": "paragraph",
      "text": "The number comes straight from the tilt of the Earth. Our planet spins on an axis that is tipped about 23.4 degrees relative to its orbit around the Sun. That tilt is what gives us seasons, and it is also what sets the latitude of the tropics. As the Earth travels around the Sun, the point where sunlight strikes vertically swings between 23.5 degrees north and 23.5 degrees south over the course of a year. The tropics simply mark the two ends of that swing."
    },
    {
      "type": "heading",
      "text": "Why the strange names?"
    },
    {
      "type": "paragraph",
      "text": "The names are a fossil from ancient astronomy. When these lines were first defined, more than two thousand years ago, the Sun at the June solstice appeared against the background stars of the constellation Cancer, and at the December solstice against Capricornus. The names stuck. Because of a slow wobble in the Earth's axis called precession, the Sun now sits in different constellations at the solstices, but the old labels have never been updated."
    },
    {
      "type": "heading",
      "text": "The Tropics as a zone"
    },
    {
      "type": "paragraph",
      "text": "The band of the planet between the two lines is called the Tropics, and it is the only region on Earth that ever receives a vertical noon sun. That concentrated sunlight is why the tropical zone is consistently warm, with small differences between seasons and, in many places, lush rainforest. The tropics also line up neatly with some of the planet's geography:"
    },
    {
      "type": "list",
      "items": [
        "The Tropic of Cancer runs through Mexico, the Sahara, Arabia, India, and southern China",
        "The Tropic of Capricorn crosses Australia, southern Africa, and South America near the edge of the Atacama and the Brazilian highlands",
        "Many of the world's great deserts sit just along these lines, where dry, sinking air dominates"
      ]
    },
    {
      "type": "heading",
      "text": "The Arctic and Antarctic Circles"
    },
    {
      "type": "paragraph",
      "text": "The tropics have two companions near the poles. The Arctic Circle and the Antarctic Circle, at about 66.5 degrees north and south, mark the opposite extreme: the latitudes beyond which the Sun stays up for a full 24 hours at midsummer and never rises at midwinter, the famous midnight sun and polar night. The two pairs of lines are linked by the same axial tilt; add the tilt to 90 degrees for the polar circles, or subtract it from 90 to get the tropics."
    },
    {
      "type": "heading",
      "text": "They are slowly moving"
    },
    {
      "type": "paragraph",
      "text": "The tropics are not fixed forever. Because the Earth's tilt slowly varies over tens of thousands of years, the two lines drift, currently creeping toward the equator by about 15 metres a year. It is far too slow to notice in a lifetime, but it is a reminder that even the lines on our maps are tied to the slow mechanics of a tilting, orbiting planet."
    },
    {
      "type": "paragraph",
      "text": "Understanding the tropics is a small key that unlocks a lot of geography at once: seasons, climate zones, and where the deserts and rainforests sit. Carry that idea into EarthGuessr and the latitude bands of the world start to make sense from above."
    }
  ]
};
